<template>
	<div class="clip-timeline position-relative user-select-none" :class="{ 'clip-timeline--compact': compact }" @wheel="onWheel">
		<!-- Section header -->
		<div class="d-flex align-center mb-1 ga-2">
			<span class="text-caption text-medium-emphasis font-weight-medium">Timeline</span>
		</div>

		<!-- Hover time label (follows cursor on the track) -->
		<div v-if="hoverTime !== null && !dragState" class="hover-time-label position-absolute text-no-wrap text-white rounded" :style="hoverTimeLabelStyle">
			{{ formatSecondsPrecise(hoverTime) }}
		</div>

		<!-- Timeline track wrapper (lets the playhead overflow the track) -->
		<div class="timeline-track-wrapper position-relative" :class="{ 'timeline-track-wrapper--compact': compact }">
			<div
				ref="timelineContainer"
				class="timeline-track position-relative h-100 overflow-hidden rounded"
				@mousedown="onTrackMouseDown"
				@touchstart.prevent="onTrackTouchStart"
				@mousemove="onTrackMouseMove"
				@mouseleave="onTrackMouseLeave">
				<!-- Clip regions -->
				<div
					v-for="(clip, index) in clips"
					:key="clip.id"
					v-show="isClipVisible(clip)"
					class="clip-region position-absolute h-100 d-flex align-center justify-center rounded"
					:class="{ 'clip-region--active': clip.id === activeClipId }"
					:style="clipRegionStyle(clip)"
					@mousedown.stop="onClipMouseDown(clip.id)"
					@touchstart.stop.prevent="onClipTouchStart(clip.id)">
					<v-chip size="x-small" variant="flat" class="clip-badge">{{ index + 1 }}</v-chip>
				</div>

				<!-- Drag preview -->
				<div v-if="dragState" class="drag-preview position-absolute h-100 rounded" :style="dragPreviewStyle"></div>
			</div>

			<!-- Playhead (outside the overflow:hidden track so the dot is never clipped) -->
			<div
				class="playhead position-absolute"
				:class="{ 'playhead--compact': compact, 'playhead--dragging': isDraggingPlayhead }"
				:style="playheadStyle">
				<!-- Only the dot area captures pointer events for seeking -->
				<div
					class="playhead-grab"
					@mousedown.stop="onPlayheadMouseDown"
					@touchstart.stop.prevent="onPlayheadTouchStart">
					<div class="playhead-hit-area"></div>
					<div class="playhead-dot rounded-circle"></div>
				</div>
				<div class="playhead-line mx-auto"></div>
			</div>
		</div>

		<!-- Drag time label (floats above the track) -->
		<div v-if="dragState" class="drag-time-label position-absolute text-no-wrap text-white rounded" :style="dragTimeLabelStyle">
			{{ dragTimeLabel }}
		</div>

		<!-- Time markers -->
		<div class="time-markers d-flex justify-space-between mt-1">
			<span v-for="(marker, i) in timeMarkers" :key="i" class="text-caption text-medium-emphasis">
				{{ marker }}
			</span>
		</div>

		<!-- Scroll slider wrapper – always takes up space so the layout never shifts -->
		<div class="scroll-slider-slot">
			<v-slider
				v-if="isZoomed"
				:model-value="scrollModel"
				:min="0"
				:max="100"
				:step="0.5"
				hide-details
				density="compact"
				color="primary"
				track-color="grey-lighten-3"
				class="scroll-slider px-1"
				@update:model-value="emit('scroll', $event)" />
		</div>
	</div>
</template>

<script setup lang="ts">
	import { ref, computed, onBeforeUnmount } from "vue";
	import { formatSeconds, formatSecondsPrecise } from "@/helpers/time";
	import { snapTimeToFrame } from "@/helpers/videoFps";
	import type { VideoClip, TimelineDragState } from "@/types/clipVideo";

	const props = withDefaults(
		defineProps<{
			duration: number;
			videoFps?: number;
			clips: VideoClip[];
			playheadTime: number;
			dragState: TimelineDragState | null;
			activeClipId: string | null;
			visibleStart?: number;
			visibleEnd?: number;
			isZoomed?: boolean;
			scrollModel?: number;
			/** Compact mode for small screens (fewer markers, bigger touch targets) */
			compact?: boolean;
		}>(),
		{
			visibleStart: 0,
			visibleEnd: 0,
			isZoomed: false,
			scrollModel: 0,
			compact: false,
			videoFps: 30
		}
	);

	const emit = defineEmits<{
		(e: "seek", time: number): void;
		(e: "drag-start", time: number): void;
		(e: "drag-move", time: number): void;
		(e: "drag-end", startX: number, endX: number): void;
		(e: "clip-click", clipId: string): void;
		(e: "scroll", position: number): void;
	}>();

	const timelineContainer = ref<HTMLElement | null>(null);
	const hoverTime = ref<number | null>(null);
	const isDraggingPlayhead = ref(false);
	let isDraggingClip = false;

	// --- Document listener cleanup tracking ---
	// Drag handlers attach listeners to `document` that are normally removed on
	// mouseup/touchend. If the component unmounts mid-drag, those listeners would
	// leak. We track pending cleanup functions so onBeforeUnmount can remove them.
	const pendingCleanups: Array<() => void> = [];

	function registerCleanup(fn: () => void) {
		pendingCleanups.push(fn);
	}

	function unregisterCleanup(fn: () => void) {
		const idx = pendingCleanups.indexOf(fn);
		if (idx >= 0) pendingCleanups.splice(idx, 1);
	}

	// --- Edge-scroll during drag (auto-scroll when dragging near edges) ---
	const EDGE_ZONE = 40; // pixels from edge to trigger auto-scroll
	const SCROLL_SPEED = 0.4; // percent per frame at edge
	let edgeScrollAnimId: number | null = null;
	let lastClientX = 0;

	function startEdgeScroll(clientX: number) {
		lastClientX = clientX;
		if (edgeScrollAnimId !== null) return; // already running
		edgeScrollLoop();
	}

	function updateEdgeScroll(clientX: number) {
		lastClientX = clientX;
	}

	function stopEdgeScroll() {
		if (edgeScrollAnimId !== null) {
			cancelAnimationFrame(edgeScrollAnimId);
			edgeScrollAnimId = null;
		}
	}

	function edgeScrollLoop() {
		if (!isDraggingClip || !props.isZoomed) {
			edgeScrollAnimId = null;
			return;
		}
		const el = timelineContainer.value;
		if (el) {
			const rect = el.getBoundingClientRect();
			const distFromLeft = lastClientX - rect.left;
			const distFromRight = rect.right - lastClientX;

			let scrollDelta = 0;
			if (distFromLeft < EDGE_ZONE && distFromLeft >= 0) {
				// Scroll left — speed increases as cursor gets closer to edge
				scrollDelta = -SCROLL_SPEED * (1 - distFromLeft / EDGE_ZONE);
			} else if (distFromRight < EDGE_ZONE && distFromRight >= 0) {
				// Scroll right
				scrollDelta = SCROLL_SPEED * (1 - distFromRight / EDGE_ZONE);
			}

			if (scrollDelta !== 0) {
				const newPercent = Math.max(0, Math.min(100, props.scrollModel + scrollDelta));
				if (newPercent !== props.scrollModel) {
					emit("scroll", newPercent);
					// Also update drag-move so the clip region extends with the scroll
					emit("drag-move", snapToFrame(pixelToTime(lastClientX)));
				}
			}
		}
		edgeScrollAnimId = requestAnimationFrame(edgeScrollLoop);
	}

	const effectiveStart = computed(() => (props.isZoomed ? props.visibleStart : 0));
	const effectiveEnd = computed(() => (props.isZoomed ? props.visibleEnd : props.duration));
	const visibleWindow = computed(() => effectiveEnd.value - effectiveStart.value);
	const normalizedVideoFps = computed(() => Math.max(1, props.videoFps || 30));

	/** Snap a time value to the nearest frame boundary. */
	function snapToFrame(time: number): number {
		return snapTimeToFrame(time, normalizedVideoFps.value);
	}

	// Show precise drag labels when the full clip is relatively short in frame terms.
	const preciseTimeThresholdSeconds = computed(() => 120 / normalizedVideoFps.value);
	const shouldUsePreciseTime = computed(
		() => props.duration > 0 && props.duration <= preciseTimeThresholdSeconds.value
	);

	function formatTimelineTime(timeSeconds: number, forceHours = false): string {
		return shouldUsePreciseTime.value
			? formatSecondsPrecise(timeSeconds, forceHours)
			: formatSeconds(timeSeconds, forceHours);
	}

	function buildDynamicMarkerTimes(start: number, end: number, maxMarkers = 5): number[] {
		if (end <= start) return [start];

		const visibleSpan = end - start;
		// For short windows (<= 4s), show every whole-second tick plus fixed start/end.
		// Examples:
		// 1s -> 0,1
		// 2s -> 0,1,2
		// 3s -> 0,1,2,3
		// 4s -> 0,1,2,3,4
		if (visibleSpan <= 4) {
			const markers = [start];
			for (let second = Math.ceil(start); second < Math.floor(end); second++) {
				if (second > start && second < end) {
					markers.push(second);
				}
			}
			markers.push(end);
			return markers;
		}

		// For 5s+ windows, show exactly `maxMarkers` equally spaced points.
		const markers: number[] = [];
		for (let i = 0; i < maxMarkers; i++) {
			markers.push(start + (i / (maxMarkers - 1)) * visibleSpan);
		}
		return markers;
	}

	const timeMarkers = computed(() => {
		if (props.duration <= 0) return ["0:00"];
		const start = effectiveStart.value;
		const visibleSpan = visibleWindow.value;
		if (visibleSpan <= 0) return ["0:00"];
		const end = start + visibleSpan;
		return buildDynamicMarkerTimes(start, end, 5).map((time) => formatSeconds(time));
	});

	function timeToPercent(seconds: number): number {
		const visibleSpan = visibleWindow.value;
		if (visibleSpan <= 0) return 0;
		return ((seconds - effectiveStart.value) / visibleSpan) * 100;
	}

	function pixelToTime(clientX: number): number {
		if (!timelineContainer.value || props.duration <= 0) return 0;
		const rect = timelineContainer.value.getBoundingClientRect();
		const percent = Math.max(0, Math.min(1, (clientX - rect.left) / rect.width));
		return effectiveStart.value + percent * visibleWindow.value;
	}

	function isClipVisible(clip: VideoClip): boolean {
		if (!props.isZoomed) return true;
		return clip.endTime > effectiveStart.value && clip.startTime < effectiveEnd.value;
	}

	function clipRegionStyle(clip: VideoClip) {
		const left = timeToPercent(clip.startTime);
		const width = timeToPercent(clip.endTime) - left;
		return {
			left: left + "%",
			width: width + "%"
		};
	}

	// Clamp the playhead so the dot never extends past the wrapper edges.
	// On mobile/iPad, ancestor containers have overflow:hidden which would clip it.
	const playheadStyle = computed(() => {
		const pct = timeToPercent(props.playheadTime);
		return {
			left: `clamp(var(--playhead-inset), ${pct}%, calc(100% - var(--playhead-inset)))`
		};
	});

	const dragPreviewStyle = computed(() => {
		if (!props.dragState) return {};
		const startPct = timeToPercent(Math.min(props.dragState.startTime, props.dragState.currentTime));
		const endPct = timeToPercent(Math.max(props.dragState.startTime, props.dragState.currentTime));
		return {
			left: startPct + "%",
			width: (endPct - startPct) + "%"
		};
	});

	// ── Drag time label ───────────────────────────────────────────────────

	const dragTimeLabel = computed(() => {
		if (!props.dragState) return "";
		const start = Math.min(props.dragState.startTime, props.dragState.currentTime);
		const end = Math.max(props.dragState.startTime, props.dragState.currentTime);
		const useHours = props.duration >= 3600;
		return `${formatTimelineTime(start, useHours)} – ${formatTimelineTime(end, useHours)}`;
	});

	const dragTimeLabelStyle = computed(() => {
		if (!props.dragState) return {};
		const cursorPct = timeToPercent(props.dragState.currentTime);
		return { left: cursorPct + "%" };
	});

	// ── Hover time label ─────────────────────────────────────────────────

	const hoverTimeLabelStyle = computed(() => {
		if (hoverTime.value === null) return {};
		const pct = timeToPercent(hoverTime.value);
		return { left: pct + "%" };
	});

	function onTrackMouseMove(event: MouseEvent) {
		if (props.dragState) return; // drag-time-label is shown instead
		hoverTime.value = snapToFrame(pixelToTime(event.clientX));
	}

	function onTrackMouseLeave() {
		hoverTime.value = null;
	}

	// --- Mouse event handlers ---

	function onTrackMouseDown(event: MouseEvent) {
		hoverTime.value = null;
		if (event.button !== 0) return;
		const startTime = snapToFrame(pixelToTime(event.clientX));
		const startX = event.clientX;
		let hasDragged = false;

		function onMouseMove(e: MouseEvent) {
			if (!hasDragged) {
				hasDragged = true;
				emit("drag-start", startTime);
				isDraggingClip = true;
				if (props.isZoomed) startEdgeScroll(e.clientX);
			}
			emit("drag-move", snapToFrame(pixelToTime(e.clientX)));
			if (props.isZoomed) updateEdgeScroll(e.clientX);
		}
		function cleanup() {
			document.removeEventListener("mousemove", onMouseMove);
			document.removeEventListener("mouseup", onMouseUp);
			unregisterCleanup(cleanup);
		}
		function onMouseUp(e: MouseEvent) {
			if (hasDragged) {
				isDraggingClip = false;
				stopEdgeScroll();
				emit("drag-end", startX, e.clientX);
			} else {
				emit("seek", startTime);
			}
			cleanup();
		}
		document.addEventListener("mousemove", onMouseMove);
		document.addEventListener("mouseup", onMouseUp);
		registerCleanup(cleanup);
	}

	function onPlayheadMouseDown(event: MouseEvent) {
		event.preventDefault();
		isDraggingPlayhead.value = true;

		function onMouseMove(e: MouseEvent) {
			emit("seek", snapToFrame(pixelToTime(e.clientX)));
		}
		function cleanup() {
			isDraggingPlayhead.value = false;
			document.removeEventListener("mousemove", onMouseMove);
			document.removeEventListener("mouseup", onMouseUp);
			unregisterCleanup(cleanup);
		}
		function onMouseUp() {
			cleanup();
		}
		document.addEventListener("mousemove", onMouseMove);
		document.addEventListener("mouseup", onMouseUp);
		registerCleanup(cleanup);
	}

	function onClipMouseDown(clipId: string) {
		emit("clip-click", clipId);
	}

	// --- Touch event handlers ---

	function onTrackTouchStart(event: TouchEvent) {
		const touch = event.touches[0];
		const startTime = snapToFrame(pixelToTime(touch.clientX));
		const startX = touch.clientX;
		let hasDragged = false;
		let lastTouchX = startX;

		function onTouchMove(e: TouchEvent) {
			e.preventDefault();
			const clientX = e.touches[0].clientX;
			lastTouchX = clientX;
			if (!hasDragged) {
				hasDragged = true;
				emit("drag-start", startTime);
				isDraggingClip = true;
				if (props.isZoomed) {
					startEdgeScroll(clientX);
				};
			}
			emit("drag-move", snapToFrame(pixelToTime(clientX)));
			if (props.isZoomed) updateEdgeScroll(clientX);
		}
		function cleanup() {
			document.removeEventListener("touchmove", onTouchMove);
			document.removeEventListener("touchend", onTouchEnd);
			unregisterCleanup(cleanup);
		}
		function onTouchEnd() {
			if (hasDragged) {
				isDraggingClip = false;
				stopEdgeScroll();
				emit("drag-end", startX, lastTouchX);
			} else {
				emit("seek", startTime);
			}
			cleanup();
		}
		document.addEventListener("touchmove", onTouchMove, { passive: false });
		document.addEventListener("touchend", onTouchEnd);
		registerCleanup(cleanup);
	}

	function onPlayheadTouchStart() {
		isDraggingPlayhead.value = true;

		function onTouchMove(e: TouchEvent) {
			e.preventDefault();
			emit("seek", snapToFrame(pixelToTime(e.touches[0].clientX)));
		}
		function cleanup() {
			isDraggingPlayhead.value = false;
			document.removeEventListener("touchmove", onTouchMove);
			document.removeEventListener("touchend", onTouchEnd);
			unregisterCleanup(cleanup);
		}
		function onTouchEnd() {
			cleanup();
		}
		document.addEventListener("touchmove", onTouchMove, { passive: false });
		document.addEventListener("touchend", onTouchEnd);
		registerCleanup(cleanup);
	}

	function onClipTouchStart(clipId: string) {
		emit("clip-click", clipId);
	}

	// --- Wheel event handler (two-finger trackpad + shift+scroll) ---

	function onWheel(event: WheelEvent) {
		if (!props.isZoomed) return;
		// Handle horizontal scroll (trackpad two-finger swipe) and shift+vertical scroll (mouse wheel)
		const deltaX = event.shiftKey ? event.deltaY : event.deltaX;
		if (deltaX === 0) return;
		event.preventDefault();

		const el = timelineContainer.value;
		if (!el) return;
		const containerWidth = el.clientWidth || 1;

		// Convert pixel delta to percentage (relative to container width for consistent feel)
		const percentDelta = (deltaX / containerWidth) * 100;
		const newPercent = Math.max(0, Math.min(100, props.scrollModel + percentDelta));
		emit("scroll", newPercent);
	}

	// --- Lifecycle cleanup ---

	onBeforeUnmount(() => {
		stopEdgeScroll();
		isDraggingClip = false;
		isDraggingPlayhead.value = false;
		for (const fn of [...pendingCleanups]) fn();
		pendingCleanups.length = 0;
	});
</script>

<style scoped>
	.clip-timeline--compact {
		padding: 10px 0 2px;
	}

	.timeline-track-wrapper {
		height: 40px;
		/* Half the playhead dot width – used by clamp() to keep the dot in-bounds */
		--playhead-inset: 7px;
	}

	.timeline-track-wrapper--compact {
		height: 48px;
		--playhead-inset: 9px;
	}

	.timeline-track {
		background-color: rgba(var(--v-theme-primary), 0.1);
		cursor: crosshair;
		touch-action: none;
	}

	.clip-region {
		top: 0;
		background-color: rgba(var(--v-theme-primary), 0.3);
		border: 1px solid rgb(var(--v-theme-primary));
		cursor: pointer;
		z-index: 1;
	}

	.clip-region--active {
		background-color: rgba(var(--v-theme-primary), 0.45);
		border-width: 2px;
	}

	.clip-badge {
		pointer-events: none;
		background-color: rgba(var(--v-theme-primary), 0.1) !important;
		color: rgb(var(--v-theme-primary)) !important;
	}

	.drag-preview {
		top: 0;
		background-color: rgba(var(--v-theme-primary), 0.15);
		border: 1px dashed rgb(var(--v-theme-primary));
		z-index: 0;
	}

	.playhead {
		top: -8px; /* to align the playhead with the timeline track */
		transform: translateX(-50%); /* to center the playhead */
		z-index: 2;
		pointer-events: none;
	}

	.playhead--compact {
		top: -10px; /* to align the playhead with the timeline track */
	}

	/* Only the grab wrapper (dot + hit area) receives pointer events */
	.playhead-grab {
		position: relative;
		pointer-events: auto;
		cursor: grab;
		display: flex;
		justify-content: center;
	}

	.playhead--dragging .playhead-grab {
		cursor: grabbing;
	}

	.playhead-grab:hover .playhead-dot,
	.playhead--dragging .playhead-dot {
		transform: scale(1.25);
		background-color: rgb(var(--v-theme-primary));
		box-shadow: 0 0 0 4px rgba(var(--v-theme-primary), 0.2);
	}

	.playhead-dot {
		width: 0.875rem;
		height: 0.875rem;
		background-color: rgb(var(--v-theme-primary));
		transition: transform 0.15s ease, background-color 0.15s ease, box-shadow 0.15s ease;
		position: relative;
		z-index: 2;
	}

	.playhead--compact .playhead-dot {
		width: 1.125rem;
		height: 1.125rem;
	}

	/* Invisible hit area scoped to the dot region */
	.playhead-hit-area {
		position: absolute;
		top: -0.375rem;
		left: 50%;
		transform: translateX(-50%);
		width: 2rem;
		height: 1.75rem;
		z-index: 1;
	}

	.playhead--compact .playhead-hit-area {
		width: 2.5rem;
		height: 2rem;
	}

	/* Line passes events through to the track beneath */
	.playhead-line {
		width: 2px;
		height: 34px;
		background-color: rgb(var(--v-theme-primary),0.3);
		pointer-events: none;
	}

	.playhead--compact .playhead-line {
		height: 40px;
	}

	.hover-time-label,
	.drag-time-label {
		top: 0;
		transform: translateX(-50%);
		font-size: 11px;
		line-height: 1;
		padding: 3px 8px;
		background-color: rgba(var(--v-theme-on-surface), 0.78);
		pointer-events: none;
		z-index: 3;
		font-variant-numeric: tabular-nums;
	}

	.time-markers {
		padding: 0 2px;
	}

	/* Fixed-height wrapper so the layout doesn't shift when the slider appears */
	.scroll-slider-slot {
		height: 20px;
		margin-top: 2px;
	}

	/* Thinner, less obtrusive scroll slider */
	.scroll-slider {
		height: 20px;
	}

	.scroll-slider :deep(.v-slider-track) {
		--v-slider-track-size: 8px;
	}
</style>
